'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import Navbar from './Navbar';
import SiteFooter from './SiteFooter';
import WhatsAppFloat from './ui/WhatsAppFloat';
import ScrollUI from './ui/ScrollUI';
import ScrollToTop from './ui/ScrollToTop';
import CookieBanner from './ui/CookieBanner';

// Lazy load Three.js ColorBends — prevents 33s+ main thread blocking on mobile
const ColorBends = dynamic(() => import('./ColorBends'), { ssr: false });

interface LayoutProps {
  children?: React.ReactNode;
}

export default function Layout({ children }: LayoutProps = {}) {
    // Delay ColorBends mount until after first paint + idle time
    const [showBg, setShowBg] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            (window as any).requestIdleCallback(() => setShowBg(true), { timeout: 2000 });
        } else {
            setTimeout(() => setShowBg(true), 1500);
        }
    }, []);

    return (
        <div className="relative min-h-screen font-sans antialiased text-slate-900 selection:bg-blue-200/60 selection:text-blue-900 overflow-x-hidden" style={{ maxWidth: '100vw', overflowX: 'clip' }}>
            {/* Fixed full-screen background — CSS gradient fallback + lazy Three.js */}
            <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: '#e8eeff' }}>
                {showBg && (
                    <ColorBends
                        colors={["#c7d9ff", "#d4c7ff", "#c7f0ff"]}
                        rotation={0}
                        speed={0.15}
                        scale={1}
                        frequency={1}
                        warpStrength={1}
                        mouseInfluence={0.5}
                        parallax={0.3}
                        noise={0.05}
                        transparent
                        autoRotate={0}
                    />
                )}
            </div>

            {/* Page content */}
            <div className="relative z-10">
                <ScrollToTop />
                <ScrollUI />
                <Navbar />
                {children}
                <WhatsAppFloat />
                <SiteFooter />
                <CookieBanner />
            </div>
        </div>
    );
}
